import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import MoveCard from './MoveCard';
import ProductThumb from './ProductThumb';
import HiddenFold from './HiddenFold';
import VetoConfirm from './VetoConfirm';
import { ColorDot, Eyebrow, SeasonBadge } from './Badges';
import {
  BRING_BACK_CHOICE_TITLE, EXCLUDE_LABEL, EXCLUDE_VARIANT_LABEL, KEPT_CHIP, SKIPPED_TODAY_CHIP,
  UNKEEP_LABEL, VetoControls, keptFoldLabel, keptRowsForRack, tileSkipKey, vetoVariantOf,
} from './exclusions';
import { variantKeepPendingKey, variantPendingKey } from './counterList';
import {
  NOW_SOURCE_NOTE, SHELF_LABEL, fixtureTiles, indexedThumbTarget, monthDay, movesOfFixture,
  shelfCountLabel, splitTiles, tileHeadline, tileOnHand, tileStates,
} from './format';
import { C, MONO, RADIUS, SHADOW, TILE_BADGE, hiddenFoldLabel } from './tokens';
import {
  RestockV2ExcludedStyle, RestockV2Fixture, RestockV2ListExcludedVariant, RestockV2Move,
  RestockV2Plan, RestockV2RackKept, RestockV2ThumbTarget, RestockV2Tile, TileState,
} from './types';

// Which tile has its confirm open. Keyed on the PAIR, not the SKU: a style with two colours on the
// same rail would otherwise open both panels at once, and the panels now say different things.
const tileVetoKey = (tile: RestockV2Tile): string => `${tile.sku}|${tile.variant_id ?? ''}`;

/**
 * Whether THIS tile's ✕ is disabled — the four keys a decision about it can be in flight under:
 * the style veto (bare SKU), the colour veto, the 留著 pair and the 今天不做 row. Keyed on the
 * sku+variant pair in every case except the style one, so one colour's decision never greys
 * another colour of the same style.
 */
function tileBusy(pending: Record<string, boolean>, tile: RestockV2Tile): boolean {
  if (pending[tile.sku]) return true;
  const id = tile.variant_id;
  if (id === null || id === undefined) return false;
  return !!pending[variantPendingKey(tile.sku, id)] || !!pending[variantKeepPendingKey(tile.sku, id)]
    || !!pending[tileSkipKey(tile.sku, id)];
}

const BADGE_STYLE: Record<TileState, { bg: string; fg: string; border?: string }> = {
  short: { bg: C.back, fg: '#fff' },
  ok: { bg: '#fff', fg: C.go, border: C.go },
  remove: { bg: C.fetch, fg: '#fff' },
  incoming: { bg: C.wait, fg: '#fff' },
};

// `qty` omitted = legend mode: the badge names the state instead of counting one tile. The count
// lives in a nested span, so the badge carries its own aria-label — that is its readable name.
export const TileBadge: React.FC<{ state: TileState; qty?: number }> = ({ state, qty }) => {
  const style = BADGE_STYLE[state];
  const base = TILE_BADGE[state];
  const legend = qty === undefined;
  return (
    <Box
      component="span"
      aria-hidden={legend ? true : undefined}
      aria-label={legend ? undefined : (state === 'ok' ? base.note : `${base.label} ${qty} 件`)}
      sx={{
        fontSize: 11, fontWeight: 800, px: '5px', borderRadius: '99px', whiteSpace: 'nowrap',
        bgcolor: style.bg, color: style.fg,
        ...(style.border ? { border: '1px solid', borderColor: style.border } : {}),
        boxShadow: '0 1px 3px rgba(0,0,0,.18)',
      }}
    >
      {state === 'ok' || (legend && state === 'incoming') ? base.label : (
        <>
          {base.label}
          <Box component="span" sx={{ fontFamily: MONO, fontWeight: 700, ml: '2px' }}>
            {legend ? 'N' : qty}
          </Box>
        </>
      )}
    </Box>
  );
};

// One target variant of this rack, as it should look after today's moves are done.
// The ✕ raises the 不要 decision: the rail plan is a PROPOSAL, so the owner must be able to strike
// something off it while standing in front of the rack (Magnus 2026-09-15). It confirms in place.
//
// A tile that carries a `variant_id` names ONE colour and ONE size, so the panel offers both scopes
// with the narrow one first (Magnus 2026-09-17 — he lost 3127 meaning to lose one size). A tile
// built from keep/add has no variant_id and keeps the original two-button panel.
const Tile: React.FC<{
  tile: RestockV2Tile; thumbIndex: Record<string, RestockV2ThumbTarget>;
  confirming: boolean; pending: boolean;
  onConfirm: (key: string | null) => void;
  veto: VetoControls;
  onImage: (target: RestockV2ThumbTarget) => void;
}> = ({ tile, thumbIndex, confirming, pending, onConfirm, veto, onImage }) => {
  const state: TileState = tile.state || 'ok';
  const target = indexedThumbTarget(thumbIndex, tile);
  const short = state === 'short';
  const variant = veto.variantsReady ? vetoVariantOf(tile) : null;
  const kept = tile.kept === true;
  const skipped = tile.skipped_today === true;
  // A 帶回 tile — or one he has already answered — opens the 帶回 verbs (今天不做 / 留著) instead of
  // the 不要 ones. It used to open nothing at all: 「a 'remove' tile is already leaving today, so
  // there is nothing to veto on it」 was true about VETOING and wrong about the tile, because 帶回
  // is the engine's PROPOSAL and he had no way to refuse it (Magnus 2026-09-17).
  // Every one of those verbs is keyed on the variant, so a tile without one still offers nothing.
  const rail = !!variant && (state === 'remove' || kept || skipped);
  const vetoable = rail || state !== 'remove';
  const headline = tileHeadline(tile);
  // The trigger says what it opens. It used to say 不要這款 on a tile that is one colour — the exact
  // mismatch Magnus paid for — so on a variant tile it names the colour instead.
  const triggerLabel = rail
    ? `${kept ? UNKEEP_LABEL : BRING_BACK_CHOICE_TITLE} ${tile.sku} ${headline}`
    : variant
      ? `${EXCLUDE_VARIANT_LABEL} ${tile.sku} ${headline}`
      : `${EXCLUDE_LABEL} ${tile.sku}`;
  const shelf = tileOnHand(tile);
  return (
    <Box
      sx={{
        position: 'relative', bgcolor: short ? '#FFFCFA' : C.card, border: '1px solid',
        borderColor: short ? '#EBBFAF' : C.line, borderRadius: '9px', p: '5px', boxShadow: SHADOW,
      }}
    >
      {/* A 留著 tile is NOT greyed: it is staying, and greying it would keep saying 「this is on its
          way off」 after he has said it is not. */}
      <Box sx={{ position: 'relative', filter: state === 'remove' && !kept ? 'grayscale(.75)' : 'none' }}>
        <ProductThumb target={target} aspect radius={6} onOpen={onImage} />
        <Box sx={{ position: 'absolute', left: 3, top: 3 }}><TileBadge state={state} qty={tile.qty} /></Box>
        {/* His own decision, on the tile, opposite corner from the engine's badge and carrying NO
            number so it can never be read as one. 「Never hide a decision he made」 — the tile stays
            on the rack either way, it just says what he answered. */}
        {(kept || skipped) && (
          <Box
            component="span" aria-label={`${tile.sku} ${headline} ${kept ? KEPT_CHIP : SKIPPED_TODAY_CHIP}`}
            sx={{
              position: 'absolute', left: 3, bottom: 3, fontSize: 10, fontWeight: 800,
              px: '5px', borderRadius: '99px', whiteSpace: 'nowrap',
              bgcolor: kept ? C.go : C.ink2, color: '#fff',
              boxShadow: '0 1px 3px rgba(0,0,0,.18)',
            }}
          >
            {kept ? KEPT_CHIP : SKIPPED_TODAY_CHIP}
          </Box>
        )}
        {vetoable && !confirming && (
          <Box
            component="button" type="button" disabled={pending}
            onClick={() => onConfirm(tileVetoKey(tile))} aria-label={triggerLabel}
            sx={{
              position: 'absolute', right: 3, top: 3, width: 24, height: 24, borderRadius: '99px',
              border: '1px solid', borderColor: C.line2, bgcolor: 'rgba(255,253,249,.92)',
              color: C.ink2, fontSize: 13, fontWeight: 800, lineHeight: 1, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              '&:disabled': { opacity: 0.5, cursor: 'default' },
            }}
          >
            ✕
          </Box>
        )}
      </Box>
      {confirming && (
        <VetoConfirm
          sku={tile.sku} variant={variant} veto={veto} onClose={() => onConfirm(null)}
          bringBack={rail ? { kept, skippedToday: skipped } : null}
        />
      )}
      <Typography
        sx={{
          fontSize: 13, fontWeight: 900, letterSpacing: '-.02em', mt: '5px', mx: '1px', lineHeight: 1.25,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
      >
        <ColorDot name={tile.color} size={9} />
        {headline}
      </Typography>
      {/* 門市現在有幾件 (Magnus 2026-09-17). Deliberately NOT a second pill on the photo: the pill is
          the ACTION (缺 1 / 帶回 1 / ✓) and this is the STOCK, and two pills a few pixels apart is
          exactly how the two would get read as one number. It sits in the text block, always with
          the word 架上 and the unit 件, and one line high so the rack grid keeps its rhythm.
          0 renders as 0 — an empty peg is a fact, not a missing value. */}
      {shelf !== null && (
        <Typography
          component="p" aria-label={`${tile.sku} ${headline} ${shelfCountLabel(shelf)}`}
          sx={{
            fontSize: 11, color: C.ink3, mx: '1px', mt: '2px', lineHeight: 1.25, fontWeight: 700,
            whiteSpace: 'nowrap', overflow: 'hidden',
          }}
        >
          {SHELF_LABEL}
          <Box
            component="span"
            sx={{ fontFamily: MONO, fontSize: 12.5, fontWeight: 800, color: C.ink, mx: '3px' }}
          >
            {shelf}
          </Box>
          件
        </Typography>
      )}
      <Typography
        sx={{
          fontSize: 12, color: C.ink3, mx: '1px', mt: '1px', lineHeight: 1.3,
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        }}
      >
        {tile.title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', mt: '2px', mx: '1px' }}>
        <Typography component="span" sx={{ fontFamily: MONO, fontSize: 12, color: C.ink3 }}>{tile.sku}</Typography>
        <SeasonBadge value={tile.season} size="xs" />
      </Box>
    </Box>
  );
};

// 現在 / 做完後 against the dotted capacity mark — the one arithmetic a part-timer needs per rack.
const CapacityRow: React.FC<{ label: string; value: number; capacity: number; scale: number; fill: string }> = ({
  label, value, capacity, scale, fill,
}) => (
  <Box aria-label={`${label} ${value} 件`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.9 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 800, color: C.ink3, width: 50, flex: 'none' }}>{label}</Typography>
    <Box sx={{ flex: 1, height: 14, bgcolor: C.paper2, borderRadius: '4px', position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute', left: 0, top: 0, bottom: 0, borderRadius: '4px', bgcolor: fill,
          width: `${Math.round((value / Math.max(scale, 1)) * 100)}%`,
        }}
      />
      <Box
        component="span" aria-hidden="true"
        sx={{
          position: 'absolute', top: -3, bottom: -3, borderLeft: '2px dotted', borderColor: C.ink2,
          left: `${Math.round((capacity / Math.max(scale, 1)) * 100)}%`,
        }}
      />
    </Box>
    <Typography sx={{ fontFamily: MONO, fontSize: 13, fontWeight: 700, width: 52, textAlign: 'right', flex: 'none' }}>
      {value} 件
    </Typography>
  </Box>
);

interface RackPanelProps {
  fixture: RestockV2Fixture;
  plan: RestockV2Plan;
  moves: RestockV2Move[];
  doneRows: Record<string, boolean>;
  pendingRows: Record<string, boolean>;
  whyRows: Record<string, boolean>;
  thumbIndex: Record<string, RestockV2ThumbTarget>;
  // Desktop packs the tiles 5-up and the cards 2-up; the phone sheet stays 3-up / 1-up.
  desktop: boolean;
  busy: boolean;
  onAdopt: () => void;
  onDone: (move: RestockV2Move) => void;
  onUndo: (move: RestockV2Move) => void;
  onSkip: (move: RestockV2Move) => void;
  skipBusy: (move: RestockV2Move) => boolean;
  // 不要這款 / 不要這個顏色／尺寸 on a target tile, and the 已排除 N 款 fold under them.
  veto: VetoControls;
  excludedStyles: RestockV2ExcludedStyle[];
  // 留著 rows whose style is no longer on this rail, so they have no tile of their own. Merged with
  // the rack's own kept tiles into one 留著 fold.
  keptOffRail?: RestockV2ListExcludedVariant[];
  onUnkeepRow?: (row: RestockV2ListExcludedVariant) => void;
  onAddBack: (sku: string) => void;
  onWhy: (id: string) => void;
  onImage: (target: RestockV2ThumbTarget) => void;
  header?: React.ReactNode;
}

// What one rack should look like, then what has to happen at it today. Shared by the phone's
// full-screen sheet and the desktop map's right-hand column, so both stay identical in substance.
const RackPanel: React.FC<RackPanelProps> = ({
  fixture, plan, moves, doneRows, pendingRows, whyRows, thumbIndex, desktop, busy,
  onAdopt, onDone, onUndo, onSkip, skipBusy, veto, excludedStyles, keptOffRail, onUnkeepRow,
  onAddBack, onWhy, onImage, header,
}) => {
  // 不要這款 confirms in place - a dialog on a phone in a shop is one tap too many, and this is a
  // decision the owner should be able to take while looking at the rail.
  const [confirming, setConfirming] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const tiles = useMemo(() => fixtureTiles(fixture, plan), [fixture, plan]);
  const split = useMemo(() => splitTiles(tiles), [tiles]);
  const shown = expanded ? tiles : split.shown;
  const now = movesOfFixture(moves, fixture.id, true);
  const later = movesOfFixture(moves, fixture.id, false);
  const arrival = monthDay(plan.meta?.expected_arrival);
  const scale = Math.max(fixture.now_pieces, fixture.next_pieces, fixture.capacity);
  const keptRows = useMemo(() => keptRowsForRack(tiles, keptOffRail || []), [tiles, keptOffRail]);
  // The engine's own sentence for 「帶回 cannot reach the target and the pieces you pinned are part
  // of why」. Non-null exactly when it is blocking, so `note` alone is the render condition — and it
  // is rendered rather than swallowed: a 帶回 list that cannot clear the rail because he kept
  // everything is precisely the thing he needs told.
  const overCapacity: RestockV2RackKept | null = plan.racks?.kept?.note ? plan.racks.kept : null;
  const cardGrid = {
    display: 'grid', gap: 1,
    gridTemplateColumns: desktop ? 'repeat(2, minmax(0, 1fr))' : undefined,
    '@media (min-width:600px)': desktop ? {} : { gridTemplateColumns: '1fr 1fr' },
  } as const;

  return (
    <Box>
      {header}
      <Box
        sx={{
          bgcolor: C.card, border: '1px solid', borderColor: C.line, borderRadius: `${RADIUS}px`,
          px: 1.4, py: 1.25, boxShadow: SHADOW,
        }}
      >
        <CapacityRow
          label="現在" value={fixture.now_pieces} capacity={fixture.capacity} scale={scale}
          fill={fixture.now_pieces > fixture.capacity ? C.back : C.ink}
        />
        <CapacityRow label="做完後" value={fixture.next_pieces} capacity={fixture.capacity} scale={scale} fill={C.go} />
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1, fontSize: 12, color: C.ink2 }}>
          <Box component="span">
            虛線 = 這個架子放得下 <Box component="b" sx={{ fontFamily: MONO }}>{fixture.capacity}</Box> 件
          </Box>
          {fixture.now_pieces > fixture.capacity && (
            <Box component="span" sx={{ color: C.back, fontWeight: 800 }}>
              現在多了 {fixture.now_pieces - fixture.capacity} 件
            </Box>
          )}
        </Box>
        {fixture.now_source === 'estimated' && (
          <Typography sx={{ fontSize: 12, color: C.ink3, mt: 0.5 }}>{NOW_SOURCE_NOTE}</Typography>
        )}
        {overCapacity && (
          <Typography
            role="note"
            aria-label={`${KEPT_CHIP}的件數超過架子容量，還多 ${overCapacity.still_over_by} 件`}
            sx={{
              fontSize: 12, fontWeight: 700, color: C.back, bgcolor: C.backWash, border: '1px solid',
              borderColor: C.back, borderRadius: '8px', px: 1, py: 0.75, mt: 1, lineHeight: 1.4,
            }}
          >
            {overCapacity.note}
          </Typography>
        )}
      </Box>

      <Eyebrow>這個架子應該長這樣</Eyebrow>
      {tiles.length === 0 && <Typography sx={{ fontSize: 13, color: C.ink2 }}>這個架子還沒有目標配置。</Typography>}
      {tiles.length > 0 && (
        <>
          <Box
            sx={{
              display: 'flex', gap: '4px 12px', flexWrap: 'wrap', mb: 1.25, px: 1.25, py: 1,
              bgcolor: C.wash, border: '1px dashed', borderColor: C.line2, borderRadius: '10px',
            }}
          >
            {tileStates(tiles).map((state) => (
              <Box
                key={state}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: 12, color: C.ink2, fontWeight: 600 }}
              >
                <TileBadge state={state} />
                {TILE_BADGE[state].note}
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'grid', gap: '7px',
              gridTemplateColumns: desktop ? 'repeat(5, minmax(0, 1fr))' : 'repeat(3, minmax(0, 1fr))',
              '@media (min-width:600px)': desktop ? {} : { gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' },
            }}
          >
            {shown.map((tile, index) => (
              <Tile
                key={`${tile.sku}:${tile.color ?? ''}:${tile.size ?? ''}:${tile.state}:${index}`}
                tile={tile} thumbIndex={thumbIndex} confirming={confirming === tileVetoKey(tile)}
                pending={tileBusy(veto.pending, tile)} onConfirm={setConfirming} veto={veto}
                onImage={onImage}
              />
            ))}
          </Box>
          {!expanded && split.rest.length > 0 && (
            <Box
              component="button" type="button" onClick={() => setExpanded(true)}
              sx={{
                width: '100%', mt: 1, py: 1.4, cursor: 'pointer', border: '1px dashed', borderColor: C.line2,
                bgcolor: C.wash, borderRadius: '10px', fontWeight: 800, fontSize: 13, color: C.ink2,
              }}
            >
              還有 {split.rest.length} 件 · 全部展開
            </Box>
          )}
        </>
      )}

      <HiddenFold
        label={hiddenFoldLabel('plan', excludedStyles.length)} styles={excludedStyles}
        pending={veto.pending} onAddBackStyle={onAddBack}
      />
      {/* 留著 gets its OWN fold, not a section of 已排除: 「I decided this stays」 and 「I decided
          this goes」 are opposite decisions and one list of them would read as one. Its rows are
          reversible with 取消留著, so a pin is never something he has to remember where he put. */}
      <HiddenFold
        label={keptFoldLabel(keptRows.length)} kept={keptRows} pending={veto.pending}
        onUnkeep={onUnkeepRow}
      />

      <Eyebrow>今天在這裡要做的 · {now.length} 件事</Eyebrow>
      {now.length === 0 ? (
        <Typography
          sx={{
            fontSize: 13, color: C.ink2, textAlign: 'center', bgcolor: C.card, border: '1px solid',
            borderColor: C.line, borderRadius: `${RADIUS}px`, px: 1.5, py: 2,
          }}
        >
          這個架子今天不用動。
        </Typography>
      ) : (
        <Box sx={cardGrid}>
          {now.map((move) => (
            <MoveCard
              key={move.id} move={move} done={!!doneRows[move.id]} pending={!!pendingRows[move.id]}
              whyOpen={!!whyRows[move.id]} arrivalLabel={arrival} skipPending={skipBusy(move)}
              onDone={onDone} onUndo={onUndo} onSkip={onSkip} onWhy={onWhy} onImage={onImage}
            />
          ))}
        </Box>
      )}

      {later.length > 0 && (
        <>
          <Eyebrow>{arrival} 到貨後才帶回 · {later.length} 件事</Eyebrow>
          <Box sx={cardGrid}>
            {later.map((move) => (
              <MoveCard
                key={move.id} move={move} done={!!doneRows[move.id]} pending={!!pendingRows[move.id]}
                whyOpen={!!whyRows[move.id]} arrivalLabel={arrival} skipPending={skipBusy(move)}
                onDone={onDone} onUndo={onUndo} onSkip={onSkip} onWhy={onWhy} onImage={onImage}
              />
            ))}
          </Box>
        </>
      )}

      {desktop && (
        <Box
          component="button" type="button" disabled={busy} onClick={onAdopt}
          sx={{
            width: '100%', mt: 2, minHeight: 48, border: 0, borderRadius: '10px', cursor: 'pointer',
            bgcolor: C.ink, color: '#FBF6EC', fontWeight: 800, fontSize: 15,
            '&:disabled': { opacity: 0.55, cursor: 'default' },
          }}
        >
          套用這個版型
        </Box>
      )}
    </Box>
  );
};

export default RackPanel;
